import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Share2, Check } from "lucide-react";

const HERO_IMG =
    "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/rwxcmxrs_Nano%20Banana%202%20-%20Professional%20YouTube%20channel%20banner%20for%20Rane%20Dynamics_%20cinematic%20sci-fi%20engineering_1%20%281%29.png";

export default function Hero() {
    const [shareFeedback, setShareFeedback] = useState(null);

    const handleShare = async () => {
        const shareData = {
            title: "Rane Dynamics — Decentralized Abundance Engineering",
            text: "Veteran-founded engineering. Sovereign Node micro-factories, the V1 electric platform, and the Spectra // Blackline trilogy.",
            url: "https://ranedynamics.com/",
        };
        try {
            if (navigator.share && typeof navigator.share === "function") {
                await navigator.share(shareData);
                setShareFeedback("shared");
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(shareData.url);
                setShareFeedback("copied");
            } else {
                setShareFeedback("error");
            }
        } catch (err) {
            if (err && err.name !== "AbortError") setShareFeedback("error");
        }
        setTimeout(() => setShareFeedback(null), 2200);
    };

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-screen bg-[#F4F4F5] pt-28 pb-16 md:pt-32 md:pb-24 overflow-hidden"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                {/* Top eyebrow */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="flex items-center gap-3 mb-10"
                >
                    <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-black/5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] text-zinc-700">
                        <ShieldCheck size={14} className="text-[#FF5500]" />
                        Veteran Founded · Est. 2024
                    </span>
                    <span className="hidden md:inline text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                        XPRIZE // AI Film Finalist Track
                    </span>
                    <button
                        onClick={handleShare}
                        data-testid="hero-share-btn"
                        aria-label="Share Rane Dynamics"
                        className={`ml-auto inline-flex items-center gap-2 border px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                            shareFeedback
                                ? "bg-[#FF5500] border-[#FF5500] text-white"
                                : "bg-white/80 hover:bg-zinc-950 hover:text-white hover:border-zinc-950 backdrop-blur border-black/10 text-zinc-700"
                        }`}
                    >
                        {shareFeedback ? <Check size={13} /> : <Share2 size={13} />}
                        <span>
                            {shareFeedback === "shared"
                                ? "Shared"
                                : shareFeedback === "copied"
                                  ? "Link copied"
                                  : shareFeedback === "error"
                                    ? "Try again"
                                    : "Share"}
                        </span>
                    </button>
                </motion.div>

                {/* Massive headline */}
                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.1 }}
                    className="font-display font-black text-[11vw] md:text-[7.5vw] leading-[0.9] tracking-[-0.04em] text-zinc-950"
                >
                    Decentralized<br />
                    <span className="italic font-light">Abundance</span><br />
                    Engineering.
                </motion.h1>

                {/* Grid split */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-16 md:mt-24 items-end">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.35 }}
                        className="md:col-span-4"
                    >
                        <p className="text-lg md:text-xl text-zinc-700 leading-relaxed max-w-md">
                            We build the infrastructure for decentralized abundance —
                            advanced mobility platforms, Sovereign Node micro-factories,
                            and the cinematic universe that shows what post-scarcity
                            actually looks like.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <a
                                href="#trilogy"
                                data-testid="hero-cta-explore"
                                className="inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#E64D00] text-white font-semibold text-sm uppercase tracking-wider px-7 py-4 rounded-full transition-all duration-300 hover:scale-105"
                            >
                                Explore The Universe
                                <ArrowRight size={16} />
                            </a>
                            <a
                                href="#movies"
                                data-testid="hero-cta-trailer"
                                className="link-underline text-sm font-semibold uppercase tracking-wider text-zinc-950"
                            >
                                Watch The Trailer
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.25 }}
                        className="md:col-span-8 relative"
                    >
                        <div className="hero-frame rounded-2xl overflow-hidden bg-black">
                            <img
                                src={HERO_IMG}
                                alt="Rane Dynamics — Decentralized Abundance Engineering. V1 electric platform and Spectra Blackline vehicle on the salt flats."
                                className="w-full h-auto object-cover"
                                data-testid="hero-image"
                            />
                        </div>
                    </motion.div>
                </div>

                {/* Marquee */}
                <div className="mt-24 md:mt-32 border-y border-black/10 py-6 overflow-hidden">
                    <div className="marquee-track flex gap-16 whitespace-nowrap text-2xl md:text-3xl font-display font-semibold text-zinc-400 uppercase tracking-tight">
                        {[
                            "Advanced Mobility",
                            "Sovereign Nodes",
                            "Trilogy Books",
                            "AI Cinema",
                            "Post-Scarcity",
                            "Veteran Engineering",
                            "Advanced Mobility",
                            "Sovereign Nodes",
                            "Trilogy Books",
                            "AI Cinema",
                            "Post-Scarcity",
                            "Veteran Engineering",
                        ].map((item, i) => (
                            <span key={i} className="flex items-center gap-16">
                                {item}
                                <span className="text-[#FF5500]">◆</span>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
